<template>
  <button
    :id="props.id"
    ref="button"
    type="button"
    :data-cy="props.dataCy"
    :class="[
      activateButtonAnimation ? 'button-animation' : '',
      'disabled:bg-tm-gray-300 disabled:text-tm-gray-600 relative flex items-center justify-center disabled:border-none',
      props.colorVariant ? `color-variant-${props.colorVariant}` : '',
      sizeClasses,
      props.btnClass,
    ]"
    :disabled="props.disabled || loading"
    @click="handleClick"
  >
    <div class="pointer-events-none absolute inset-0 flex items-center justify-center">
      <GenericLoadingSpinner
        class="transition-opacity duration-200"
        :class="[loading ? 'opacity-100' : 'opacity-0']"
        data-cy="genericButtonLoadingSpinner"
        icon-size="size-4.5"
      />
    </div>
    <div
      class="flex items-center justify-center transition-opacity duration-200"
      :class="[
        loading ? 'invisible opacity-0' : 'opacity-100',
        props.stacked
          ? 'flex-col-reverse gap-2.5'
          : reversedOrder
            ? 'flex-row-reverse'
            : 'flex-row',
      ]"
    >
      <GenericIcon v-if="props.icon" :icon="props.icon" :icon-size="resolvedIconSize" />
      <div
        v-if="props.text || props.subText"
        class="flex flex-col gap-0.5"
        :class="[
          props.icon && !props.stacked ? (reversedOrder ? 'mr-2.5' : 'ml-2.5') : '',
          props.textClass,
        ]"
      >
        <span
          v-if="props.text"
          class="font-bold whitespace-nowrap"
          :class="props.size === 'small' ? 'text-xs' : 'text-sm'"
          data-cy="genericButtonText"
          >{{ props.text }}</span
        >
        <span v-if="props.subText" class="truncate text-xs font-normal">{{ props.subText }}</span>
      </div>
      <slot />
    </div>
  </button>
</template>
<script setup lang="ts">
import { ColorVariants, type ControlSize, type MingcuteIconName } from '@therame/design-tokens';
import GenericIcon, { type IconSize } from '../GenericIcon/GenericIcon.vue';
import { GenericLoadingSpinner } from '../GenericLoadingSpinner';
import { Comment, Text, computed, useSlots } from 'vue';

export interface GenericButtonProps {
  dataCy?: string;
  size?: ControlSize;
  text?: string;
  subText?: string;
  icon?: MingcuteIconName;
  /** Maps to a `color-variant-*` utility (bg + text/border colors). Single source of truth for button color. */
  colorVariant: ColorVariants;
  btnClass?: string;
  iconSize?: IconSize;
  id?: string;
  iconClass?: string;
  textClass?: string;
  disabled?: boolean;
  reversedOrder?: boolean;
  /** Stacks the label above the icon (instead of beside it), with a gap in place of the text margin. */
  stacked?: boolean;
  loading?: boolean;
  activateButtonAnimation?: boolean;
  confirmClick?: (event: MouseEvent) => void;
}

const props = withDefaults(defineProps<GenericButtonProps>(), {
  dataCy: undefined,
  size: 'default',
  text: '',
  subText: '',
  icon: undefined,
  btnClass: '',
  iconSize: undefined,
  textClass: '',
  disabled: false,
  reversedOrder: false,
  stacked: false,
  loading: false,
  activateButtonAnimation: true,
  confirmClick: () => {},
  id: undefined,
});

const slots = useSlots();

/**
 * True when the default slot actually renders visible content. We can't rely on
 * `!!slots.default` or the slot's raw length: some wrappers (e.g. Headless UI's
 * `MenuButton :as`) always pass a default slot function down even with no
 * content, and Vue normalises that empty slot into a single comment vnode
 * (`<!---->`). Either would make an icon-only button look non-empty and lose its
 * square shape, so we ignore comment and whitespace-only text nodes.
 */
const hasSlotContent = computed(() => {
  const nodes = slots.default?.();
  return (
    nodes?.some((node) => {
      if (node.type === Comment) return false;
      if (node.type === Text) return String(node.children).trim() !== '';
      return true;
    }) ?? false
  );
});

/** An icon with no text, subText, or slotted content — rendered as a square. */
const isIconOnly = computed(
  () => !!props.icon && !props.text && !props.subText && !hasSlotContent.value,
);

/**
 * Height, radius, and horizontal padding per size, matching the Figma button component.
 * Icon-only buttons drop the padding and floor their width to the height (via `min-w-*`), so
 * they render square without a per-call width. `shrink-0` keeps them square inside a flex row.
 * Floors (not a fixed `size-*`) are deliberate: a bare-icon button that wants to be smaller
 * opts out with `!min-w-*`/`!min-h-*` (as `GenericModalTemplate`, the treatment-plan forms, and
 * others do) — a fixed `size-*` would ignore those overrides and force a 44px square. A larger
 * explicit width still wins via `!w-*`.
 *
 * A single-line button needs no vertical padding — `min-h-*` plus centred content does the
 * spacing. A `stacked` one outgrows that floor, so it pays for its own
 * breathing room with `py-2.5` instead of sitting flush against the top and bottom edges.
 * (`stacked` = label above icon, so the content is two rows tall.)
 */
const sizeClasses = computed(() => {
  const isSmall = props.size === 'small';
  if (isIconOnly.value) {
    return isSmall
      ? 'min-h-8 min-w-8 shrink-0 rounded-lg px-0'
      : 'min-h-11 min-w-11 shrink-0 rounded-xl px-0';
  }
  const shape = isSmall ? 'min-h-8 rounded-lg' : 'min-h-11 rounded-xl';
  const padding = `${isSmall ? 'px-2.5' : 'px-5'}${props.stacked ? ' py-2.5' : ''}`;
  return `${shape} ${padding}`;
});

/**
 * Icon size defaults to the button size — `size-4.5` for `small`, `size-5` for `normal` —
 * matching the Figma button component. An explicit `iconSize` prop still wins.
 */
const resolvedIconSize = computed<IconSize>(
  () => props.iconSize ?? (props.size === 'small' ? 'size-4.5' : 'size-5'),
);

const emit = defineEmits<{ (event: 'confirmClick', payload: MouseEvent): void }>();

const handleClick = (event: MouseEvent) => {
  props.confirmClick?.(event);
  emit('confirmClick', event);
};
</script>
